import React from "react";
import { Flex, Text } from "@chakra-ui/react";
import { useColorThemeToggle } from "hooks/use-color-theme-toggle";
import { type BrandColor, type ThemeBgColor } from "contexts/color-theme";
import { Container } from "ui/container";
import { HightouchStatus } from "./hightouch-status";
import { FooterCTA, type FooterCTAProps } from "./cta";
import { NavColumns } from "./nav-columns";

export type FooterProps = {
  /**
   * @default "green"
   */
  variant?: BrandColor;

  /**
   * Used to fully remove the CTA section from the footer.
   * Should only be used sparingly in cases where the CTA might act as a distraction from conversion (e.g. /demo page).
   */
  hideCta?: boolean; // eslint-disable-line react/boolean-prop-naming

  /**
   * Used to pass overrides to the footer CTA component, when custom copy or actions are required.
   */
  ctaProps?: Omit<FooterCTAProps, "variant">;
};

export function Footer({ variant = "green", hideCta, ctaProps }: FooterProps) {
  const bgColor: ThemeBgColor = `new.${variant}.950`;
  const scope = useColorThemeToggle({ theme: "dark", bgColor });

  return (
    <Flex
      ref={scope}
      as="footer"
      bg={bgColor}
      pos="relative"
      flexDir="column"
      align="center"
      overflow="hidden"
      pt="huge"
      gap="huge"
    >
      {!hideCta && <FooterCTA variant={variant} {...ctaProps} />}

      <Container
        center
        pos="relative"
        zIndex="1"
        color="brand.text.dark"
        pb={{ base: "biggest", lg: "huge" }}
        gap="biggest"
      >
        <NavColumns />
        <Flex
          align={{ base: "start", sm: "center" }}
          direction={{ base: "column", sm: "row" }}
          justify={{ base: "start", sm: "space-between" }}
          gap={{ base: "big", sm: "bigger" }}
          width="100%"
        >
          <HightouchStatus />
          <Text textStyle="caption2" color="brand.textSecondary.dark" opacity={0.85} flexShrink={0}>
            Hightouch © {new Date().getFullYear()}
          </Text>
        </Flex>
      </Container>
    </Flex>
  );
}
